import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Select,
  Tag,
  Space,
  Descriptions,
  Row,
  Col,
  Typography,
  Divider,
  Badge,
  Empty,
  Button,
  Spin,
  Timeline,
  Drawer,
  Alert,
  Statistic,
  DatePicker,
} from 'antd';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import {
  Package,
  Activity,
  Clock,
  CheckCircle2,
  AlertCircle,
  Loader,
  Calendar,
  User,
  MapPin,
  Hash,
  RefreshCw,
  ChevronRight,
  Droplet,
  PlayCircle,
  StopCircle,
  Info,
} from 'lucide-react';
import { get_equipment_logs, get_equipment_list_for_cleaning } from '@/utils/api';
import { defaultColors } from '@/themes/antd-theme';
import { useTheme } from '@/App';

dayjs?.extend(duration);

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const EquipmentLogs = () => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [loading, setLoading] = useState(false);

  // Dark mode colors
  const drawerBgColor = isDark ? defaultColors.palette.gray[800] : '#ffffff';
  const cardBgColor = isDark ? defaultColors.palette.gray[700] : '#ffffff';
  const textColor = isDark ? '#ffffff' : '#1f2937';
  const secondaryTextColor = isDark ? defaultColors.palette.gray[400] : '#6b7280';
  const descriptionsBgColor = isDark ? 'transparent' : '#fafafa';
  const descriptionsLabelBg = isDark ? 'rgba(255,255,255,0.1)' : '#fafafa';
  const borderColor = isDark ? defaultColors.palette.gray[600] : '#e5e7eb';
  const timelineDescBgColor = isDark ? 'rgba(255,255,255,0.05)' : '#ffffff';
  const timelineDescLabelBg = isDark ? 'rgba(255,255,255,0.1)' : '#f5f5f5';
  const [equipmentList, setEquipmentList] = useState([]);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [dateRange, setDateRange] = useState([dayjs().subtract(30, 'days'), dayjs()]);
  const [logData, setLogData] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);

  // Fetch equipment list on mount
  useEffect(() => {
    fetchEquipmentList();
  }, []);

  const fetchEquipmentList = async () => {
    try {
      await get_equipment_list_for_cleaning((res) => {
        if (res.data?.status === 'success' && res.data?.data) {
          setEquipmentList(res.data.data);
        } else {
          toast.warning('No equipment found');
          setEquipmentList([]);
        }
      });
    } catch (error) {
      console.error('Error fetching equipment list:', error);
      toast.error('Failed to load equipment list');
      setEquipmentList([]);
    }
  };

  const fetchEquipmentLogs = async (equipmentId, startDate, endDate) => {
    setLoading(true);
    try {
      const params = {
        equipment_id: equipmentId,
        start_date: startDate,
        end_date: endDate,
      };

      await get_equipment_logs(params, (res) => {
        if (res.status === 'success') {
          setLogData(res.data);
          toast.success('Equipment logs loaded successfully');
        } else {
          toast.error(res.message || 'Failed to fetch equipment logs');
          setLogData(null);
        }
        setLoading(false);
      });
    } catch (error) {
      console.error('Error fetching equipment logs:', error);
      toast.error('Failed to load equipment logs');
      setLoading(false);
    }
  };

  const handleEquipmentChange = (equipmentId) => {
    setSelectedEquipment(equipmentId);
    // if (equipmentId && dateRange && dateRange[0] && dateRange[1]) {
    //   const startDate = dateRange[0].format('YYYY-MM-DD');
    //   const endDate = dateRange[1].format('YYYY-MM-DD');
    //   // fetchEquipmentLogs(equipmentId, startDate, endDate);
    // } else {
    //   setLogData(null);
    // }
  };

  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
    if (selectedEquipment && dates && dates[0] && dates[1]) {
      const startDate = dates[0].format('YYYY-MM-DD');
      const endDate = dates[1].format('YYYY-MM-DD');
      fetchEquipmentLogs(selectedEquipment, startDate, endDate);
    }
  };

  const handleRefresh = () => {
    if (selectedEquipment && dateRange && dateRange[0] && dateRange[1]) {
      const startDate = dateRange[0].format('YYYY-MM-DD');
      const endDate = dateRange[1].format('YYYY-MM-DD');
      fetchEquipmentLogs(selectedEquipment, startDate, endDate);
      toast.info('Refreshing equipment logs...');
    }
  };

  const getStatusConfig = (status) => {
    const configs = {
      Completed: { color: 'success', icon: <CheckCircle2 size={16} />, text: 'Completed' },
      'In Progress': { color: 'processing', icon: <Loader size={16} />, text: 'In Progress' },
      Pending: { color: 'warning', icon: <Clock size={16} />, text: 'Pending' },
      Failed: { color: 'error', icon: <AlertCircle size={16} />, text: 'Failed' },
    };
    return configs[status] || configs['Pending'];
  };

  const getMachineStatusConfig = (status) => {
    const configs = {
      Available: {
        color: 'success',
        bgColor: isDark ? '#14532d' : '#f0fdf4',
        borderColorVal: isDark ? '#166534' : '#bbf7d0',
      },
      Occupied: {
        color: 'processing',
        bgColor: isDark ? '#1e3a5f' : '#eff6ff',
        borderColorVal: isDark ? '#1e40af' : '#bfdbfe',
      },
      Cleaning: {
        color: 'warning',
        bgColor: isDark ? '#713f12' : '#fefce8',
        borderColorVal: isDark ? '#a16207' : '#fef08a',
      },
      Maintenance: {
        color: 'default',
        bgColor: isDark ? defaultColors.palette.gray[700] : '#f9fafb',
        borderColorVal: isDark ? defaultColors.palette.gray[600] : '#e5e7eb',
      },
      'Under Repair': {
        color: 'error',
        bgColor: isDark ? '#7f1d1d' : '#fef2f2',
        borderColorVal: isDark ? '#991b1b' : '#fecaca',
      },
    };
    return configs[status] || configs['Available'];
  };

  const getLogTypeIcon = (logType) => {
    const icons = {
      MACHINE_STATUS_CHANGE: <Activity size={16} className="text-blue-600" />,
      CLEANING_ACTIVITY: <Droplet size={16} className="text-cyan-600" />,
      CLEANING_APPROVAL: <CheckCircle2 size={16} className="text-green-600" />,
      BATCH_START: <PlayCircle size={16} className="text-green-600" />,
      BATCH_END: <StopCircle size={16} className="text-red-600" />,
    };
    return icons[logType] || <Info size={16} className="text-gray-600" />;
  };

  const formatDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return 'Ongoing';
    const start = dayjs(startTime);
    const end = dayjs(endTime);
    const diff = end.diff(start);
    const duration = dayjs.duration(diff);

    const hours = Math.floor(duration.asHours());
    const minutes = duration.minutes();

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const batchColumns = [
    {
      title: 'Batch Number',
      dataIndex: 'batch_number',
      key: 'batch_number',
      width: 180,
      render: (text) => (
        <Text strong className="text-blue-600">
          {text}
        </Text>
      ),
    },
    {
      title: 'Recipe Name',
      dataIndex: 'recipe_name',
      key: 'recipe_name',
      width: 150,
    },
    {
      title: 'Product',
      key: 'product',
      width: 150,
      render: (_, record) => (
        <div>
          <Text className="block text-sm">{record.product_name}</Text>
          <Text type="secondary" className="text-xs">
            {record.product_code}
          </Text>
        </div>
      ),
    },
    {
      title: 'Start Time',
      dataIndex: 'batch_start_time',
      key: 'batch_start_time',
      width: 180,
      render: (time) => (
        <Space size={4}>
          <Calendar size={14} className="text-gray-400" />
          <Text className="text-xs">{dayjs(time).format('DD-MMM-YYYY HH:mm')}</Text>
        </Space>
      ),
    },
    {
      title: 'End Time',
      dataIndex: 'batch_end_time',
      key: 'batch_end_time',
      width: 180,
      render: (time) =>
        time ? (
          <Space size={4}>
            <Calendar size={14} className="text-gray-400" />
            <Text className="text-xs">{dayjs(time)?.format('DD-MMM-YYYY HH:mm')}</Text>
          </Space>
        ) : (
          <Tag
            color="orange"
            className="inline-flex items-center gap-1 text-xs px-4 py-1 rounded-full bg-orange-100 text-orange-700 border border-orange-300"
          >
            <Clock size={12} />
            Ongoing
          </Tag>
        ),
    },
    {
      title: 'Duration',
      key: 'duration',
      width: 100,
      render: (_, record) => (
        <Tag color="blue" className="text-xs rounded-full">
          {formatDuration(record.batch_start_time, record.batch_end_time)}
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'batch_status',
      key: 'batch_status',
      width: 130,
      render: (status) => {
        const config = getStatusConfig(status);
        return (
          <Tag
            color={config.color}
            icon={config.icon}
            className="inline-flex items-center gap-1 text-xs px-4 py-1 rounded-full bg-orange-100 text-orange-700 border border-orange-300"
          >
            {config.text}
          </Tag>
        );
      },
    },
    {
      title: 'Logs',
      dataIndex: 'logs',
      key: 'logs',
      width: 100,
      align: 'center',
      render: (logs) => (
        <Badge count={logs?.length || 0} showZero color="blue" className="cursor-pointer" />
      ),
    },
    {
      title: 'Action',
      key: 'action',
      width: 120,
      fixed: 'right',
      align: 'center',
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          onClick={() => {
            setSelectedBatch(record);
            setDrawerVisible(true);
          }}
          icon={<ChevronRight size={24} />}
          className="flex items-center"
        >
          View Details
        </Button>
      ),
    },
  ];

  const renderEquipmentDetails = () => {
    if (!logData?.equipment_details) return null;

    const { equipment_details } = logData;
    const statusConfig = getMachineStatusConfig(equipment_details.current_status);

    return (
      <Card className="mb-6 shadow-sm border-l-4 border-blue-500">
        <Row gutter={16}>
          <Col span={24}>
            <div className="flex justify-between items-start mb-4">
              <Space direction="vertical" size={2}>
                <Title level={4} className="!mb-0 text-blue-700">
                  {equipment_details.equipment_name}
                </Title>
                <Text type="secondary" className="text-sm">
                  {equipment_details.equipment_type} - {equipment_details.machine_category}
                </Text>
              </Space>
              <Tag
                color={statusConfig.color}
                className="px-4 py-1 text-sm font-semibold border"
                style={{
                  backgroundColor: statusConfig.bgColor,
                  borderColor: statusConfig.borderColorVal,
                }}
              >
                {equipment_details.current_status}
              </Tag>
            </div>
          </Col>
        </Row>

        <Descriptions bordered size="small" column={{ xs: 1, sm: 2, md: 3, lg: 4 }}>
          <Descriptions.Item
            label={
              <>
                <Hash size={14} className="inline mr-1" />
                Equipment ID
              </>
            }
          >
            <Text strong>{equipment_details.equipment_code}</Text>
          </Descriptions.Item>
          <Descriptions.Item
            label={
              <>
                <MapPin size={14} className="inline mr-1" />
                Area
              </>
            }
          >
            {equipment_details.area}
          </Descriptions.Item>
          <Descriptions.Item
            label={
              <>
                <MapPin size={14} className="inline mr-1" />
                Location
              </>
            }
          >
            {equipment_details.location}
          </Descriptions.Item>
          <Descriptions.Item
            label={
              <>
                <User size={14} className="inline mr-1" />
                Created By
              </>
            }
          >
            {equipment_details.created_by?.employee_name}
          </Descriptions.Item>
          <Descriptions.Item
            label={
              <>
                <Calendar size={14} className="inline mr-1" />
                Created At
              </>
            }
            span={2}
          >
            {dayjs(equipment_details.created_at).format('DD-MMM-YYYY HH:mm:ss')}
          </Descriptions.Item>
        </Descriptions>
      </Card>
    );
  };

  const renderSummary = () => {
    if (!logData?.summary) return null;

    const { summary } = logData;

    return (
      <Card className="mb-6 shadow-sm">
        <Title level={5} className="mb-4">
          <Package size={20} className="inline mr-2 text-blue-600" />
          Summary Statistics
        </Title>
        <Row gutter={[16, 16]}>
          <Col xs={12} sm={8} md={4}>
            <Card size="small" className="h-full text-center">
              <Statistic
                title="Total Batches"
                value={summary.total_batches}
                prefix={<Package size={16} className="text-blue-600" />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={4}>
            <Card size="small" className="h-full text-center">
              <Statistic
                title="Total Logs"
                value={summary.total_logs}
                prefix={<Activity size={16} className="text-purple-600" />}
                valueStyle={{ color: '#722ed1' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={4}>
            <Card size="small" className="h-full text-center">
              <Statistic
                title="Completed"
                value={summary.batches_completed}
                prefix={<CheckCircle2 size={16} className="text-green-600" />}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={4}>
            <Card size="small" className="h-full text-center">
              <Statistic
                title="In Progress"
                value={summary.batches_in_progress}
                prefix={<Loader size={16} className="text-orange-600" />}
                valueStyle={{ color: '#fa8c16' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={4}>
            <Card size="small" className="h-full text-center">
              <Statistic
                title="Status Changes"
                value={summary.total_status_changes}
                prefix={<Activity size={16} className="text-cyan-600" />}
                valueStyle={{ color: '#13c2c2' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={4}>
            <Card size="small" className="h-full text-center">
              <Statistic
                title="Cleaning Activities"
                value={summary.total_cleaning_activities}
                prefix={<Droplet size={16} className="text-blue-400" />}
                valueStyle={{ color: '#40a9ff' }}
              />
            </Card>
          </Col>
        </Row>
      </Card>
    );
  };

  const renderBatchLogs = () => {
    if (!logData?.batches || logData.batches.length === 0) {
      return (
        <Card className="mt-6">
          <Empty description="No batch logs found for this equipment" />
        </Card>
      );
    }

    return (
      <Card className="mt-6 shadow-sm">
        <div className="flex justify-between items-center mb-4">
          <Title level={5} className="!mb-0">
            <Activity size={20} className="inline mr-2 text-blue-600" />
            Batch Processing History
          </Title>
          <Tag color="blue" className="text-sm">
            {logData.batches.length} Batches
          </Tag>
        </div>

        <Table
          columns={batchColumns}
          dataSource={logData.batches}
          rowKey="batch_number"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50'],
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} batches`,
          }}
          scroll={{ x: 1200 }}
          size="small"
          bordered
        />
      </Card>
    );
  };

  const renderLogTimeline = (logs) => {
    if (!logs || logs.length === 0) {
      return <Empty description="No logs available for this batch" className="my-4" />;
    }

    return (
      <Timeline mode="left" className="mt-4">
        {logs.map((log, index) => {
          const statusConfig = getMachineStatusConfig(log.machine_status);

          return (
            <Timeline.Item
              key={log.log_id}
              color={statusConfig.color}
              dot={getLogTypeIcon(log.log_type)}
            >
              <Card
                size="small"
                style={{
                  backgroundColor: statusConfig.bgColor,
                  borderColor: statusConfig.borderColorVal,
                  border: `1px solid ${statusConfig.borderColorVal}`,
                }}
              >
                <div className="flex justify-between items-start mb-2">
                  <Space>
                    <Tag color={statusConfig.color} className="font-semibold">
                      Stage {log.stage_number}
                    </Tag>
                    <Text strong className="text-sm" style={{ color: textColor }}>
                      {log.stage_name}
                    </Text>
                  </Space>
                  <Tag color="blue" className="text-xs">
                    {log.log_type?.replace(/_/g, ' ')}
                  </Tag>
                </div>

                <Descriptions
                  size="small"
                  column={2}
                  className="mb-2 rounded"
                  contentStyle={{ backgroundColor: timelineDescBgColor, color: textColor }}
                  labelStyle={{ backgroundColor: timelineDescLabelBg, color: secondaryTextColor }}
                >
                  <Descriptions.Item label="Action">
                    <span style={{ color: textColor }}>{log.action_type}</span>
                  </Descriptions.Item>
                  <Descriptions.Item label="Status">
                    <Tag color={statusConfig.color}>{log?.machine_status}</Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Timestamp">
                    <span style={{ color: textColor }}>
                      {dayjs(log.timestamp).format('DD-MMM-YYYY HH:mm:ss')}
                    </span>
                  </Descriptions.Item>
                  <Descriptions.Item label="Performed By">
                    <Space size={4} style={{ color: textColor }}>
                      <User size={14} />
                      {log.performed_by?.employee_name}
                    </Space>
                  </Descriptions.Item>
                  {log.start_time && (
                    <Descriptions.Item label="Start Time">
                      <span style={{ color: textColor }}>
                        {dayjs(log.start_time).format('DD-MMM-YYYY HH:mm')}
                      </span>
                    </Descriptions.Item>
                  )}
                  {log.end_time && (
                    <Descriptions.Item label="End Time">
                      <span style={{ color: textColor }}>
                        {dayjs(log.end_time).format('DD-MMM-YYYY HH:mm')}
                      </span>
                    </Descriptions.Item>
                  )}
                  {log.cleaning_type && (
                    <Descriptions.Item label="Cleaning Type" span={2}>
                      <span style={{ color: textColor }}>
                        {log.cleaning_type.name} ({log.cleaning_type.category})
                      </span>
                    </Descriptions.Item>
                  )}
                  {log.verification_method && (
                    <Descriptions.Item label="Verification Method">
                      <span style={{ color: textColor }}>{log.verification_method}</span>
                    </Descriptions.Item>
                  )}
                  {log.verification_results && (
                    <Descriptions.Item label="Verification Results">
                      <span style={{ color: textColor }}>{log.verification_results}</span>
                    </Descriptions.Item>
                  )}
                  {log.cleaning_agents_used && (
                    <Descriptions.Item label="Cleaning Agents">
                      <span style={{ color: textColor }}>{log.cleaning_agents_used}</span>
                    </Descriptions.Item>
                  )}
                  {log.duration_minutes && (
                    <Descriptions.Item label="Duration">
                      <Tag color="blue">{log.duration_minutes} minutes</Tag>
                    </Descriptions.Item>
                  )}
                  {log.approval_comments && (
                    <Descriptions.Item label="Approval Comments" span={2}>
                      <span style={{ color: textColor }}>{log.approval_comments}</span>
                    </Descriptions.Item>
                  )}
                </Descriptions>

                {log.remarks && (
                  <Alert
                    message="Remarks"
                    description={log.remarks}
                    type="info"
                    showIcon
                    className="text-xs"
                    style={{
                      backgroundColor: isDark ? '#1e3a5f' : undefined,
                      borderColor: isDark ? '#1e40af' : undefined,
                    }}
                  />
                )}
              </Card>
            </Timeline.Item>
          );
        })}
      </Timeline>
    );
  };

  const renderBatchDetailsDrawer = () => {
    if (!selectedBatch) return null;

    const statusConfig = getStatusConfig(selectedBatch.batch_status);

    return (
      <Drawer
        title={
          <Space>
            <Package size={20} className="text-blue-600" />
            <span style={{ color: textColor }}>Batch Details: {selectedBatch.batch_number}</span>
          </Space>
        }
        placement="right"
        width={720}
        onClose={() => {
          setDrawerVisible(false);
          setSelectedBatch(null);
        }}
        open={drawerVisible}
        styles={{
          header: { backgroundColor: drawerBgColor, borderBottom: `1px solid ${borderColor}` },
          body: { backgroundColor: drawerBgColor },
        }}
      >
        <Card
          className="mb-4 shadow-sm"
          style={{ backgroundColor: cardBgColor, borderColor: borderColor }}
        >
          <Descriptions
            bordered
            size="small"
            column={2}
            contentStyle={{ backgroundColor: descriptionsBgColor, color: textColor }}
            labelStyle={{ backgroundColor: descriptionsLabelBg, color: secondaryTextColor }}
          >
            <Descriptions.Item label="Batch Number" span={2}>
              <Text strong className="text-blue-600">
                {selectedBatch.batch_number}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="Recipe Name">
              <span style={{ color: textColor }}>{selectedBatch.recipe_name}</span>
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag
                className="inline-flex items-center gap-1 text-xs px-4 py-1  bg-orange-100 text-orange-700 border border-orange-300"
                color={statusConfig.color}
                icon={statusConfig.icon}
              >
                {statusConfig.text}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Product Name">
              <span style={{ color: textColor }}>{selectedBatch.product_name}</span>
            </Descriptions.Item>
            <Descriptions.Item label="Product Code">
              <span style={{ color: textColor }}>{selectedBatch.product_code}</span>
            </Descriptions.Item>
            <Descriptions.Item label="Start Time" span={2}>
              <span style={{ color: textColor }}>
                {dayjs(selectedBatch.batch_start_time).format('DD-MMM-YYYY HH:mm:ss')}
              </span>
            </Descriptions.Item>
            {selectedBatch.batch_end_time && (
              <Descriptions.Item label="End Time" span={2}>
                <span style={{ color: textColor }}>
                  {dayjs(selectedBatch.batch_end_time).format('DD-MMM-YYYY HH:mm:ss')}
                </span>
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Duration" span={2}>
              <Tag color="blue" className="text-sm">
                {formatDuration(selectedBatch.batch_start_time, selectedBatch.batch_end_time)}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Total Logs" span={2}>
              <Badge count={selectedBatch.logs?.length || 0} showZero color="blue" />
            </Descriptions.Item>
          </Descriptions>
        </Card>

        <Divider orientation="left" style={{ borderColor: borderColor }}>
          <Space style={{ color: textColor }}>
            <Activity size={18} />
            Activity Timeline
          </Space>
        </Divider>

        <div
          className="overflow-y-auto pr-2"
          style={{
            maxHeight: 'calc(100vh - 500px)',
          }}
        >
          {renderLogTimeline(selectedBatch.logs)}
        </div>
      </Drawer>
    );
  };

  return (
    <div className="p-2.5">
      <Spin spinning={loading} tip="Loading equipment logs...">
        <Card
          className="border border-gray-300 shadow-md"
          title={
            <div className="flex items-center gap-2">
              <Activity className="text-blue-600" size={24} />
              <Title
                level={4}
                style={{
                  color: textColor,
                  margin: 0,
                  padding: 0,
                }}
              >
                {t('Equipment Logs')}
              </Title>
            </div>
          }
          extra={
            <Space>
              <Button
                type="text"
                icon={<RefreshCw size={16} />}
                onClick={handleRefresh}
                disabled={!selectedEquipment}
                className="flex items-center gap-1"
              >
                Refresh
              </Button>
              <Tag color="blue" className="inline-flex items-center gap-1">
                <Activity size={14} />
                <span>Equipment Tracking</span>
              </Tag>
            </Space>
          }
        >
          <Row gutter={[16, 16]} className="mb-6" align="bottom">
            <Col xs={24} sm={12} md={8}>
              <Space direction="vertical" className="w-full" size="small">
                <Text strong>Select Equipment</Text>
                <Select
                  showSearch
                  placeholder="Select equipment to view logs"
                  value={selectedEquipment}
                  onChange={handleEquipmentChange}
                  style={{ width: '100%' }}
                  size="large"
                  allowClear
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {equipmentList?.map((equipment) => (
                    <Option key={equipment.equipment_id} value={equipment.equipment_id}>
                      {equipment.equipment_name}
                      {equipment.equipment_code && ` (${equipment.equipment_code})`}
                    </Option>
                  ))}
                </Select>
              </Space>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Space direction="vertical" className="w-full" size="small">
                <Text strong>Date Range</Text>
                <RangePicker
                  value={dateRange}
                  onChange={handleDateRangeChange}
                  style={{ width: '100%' }}
                  size="large"
                  format="DD-MMM-YYYY"
                  placeholder={['Start Date', 'End Date']}
                  disabled={!selectedEquipment}
                  allowClear={false}
                />
              </Space>
            </Col>
            <Col xs={24} sm={24} md={4}>
              <Button
                type="primary"
                icon={<RefreshCw size={16} />}
                onClick={handleRefresh}
                disabled={!selectedEquipment}
                className="w-full bg-[#204448]"
                size="large"
              >
                Load Logs
              </Button>
            </Col>
          </Row>

          {!selectedEquipment && !logData && (
            <Card className="mt-6">
              <Empty description="Please select an equipment to view logs" />
            </Card>
          )}

          {logData && (
            <>
              {renderEquipmentDetails()}
              {renderSummary()}
              {renderBatchLogs()}
            </>
          )}
        </Card>

        {renderBatchDetailsDrawer()}
      </Spin>
    </div>
  );
};

export default EquipmentLogs;
